import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal } from "lucide-react";
import { LocationCascade } from "@/components/LocationCascade";

export default function Catalog() {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";
  const minPrice = params.get("min") || "";
  const maxPrice = params.get("max") || "";
  const locKeys = ["region", "prefecture", "commune", "canton", "localite"];
  const loc = Object.fromEntries(locKeys.map((k) => [k, params.get(k) || ""]));
  const [searchInput, setSearchInput] = useState(search);
  const [showFilters, setShowFilters] = useState(false);
  const [minInput, setMinInput] = useState(minPrice);
  const [maxInput, setMaxInput] = useState(maxPrice);

  useEffect(() => { setMinInput(minPrice); setMaxInput(maxPrice); }, [minPrice, maxPrice]);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    setProducts(null);
    const qs = new URLSearchParams();
    if (category !== "all") qs.set("category", category);
    if (search) qs.set("search", search);
    if (minPrice) qs.set("min_price", minPrice);
    if (maxPrice) qs.set("max_price", maxPrice);
    locKeys.forEach((k) => loc[k] && qs.set(k, loc[k]));
    qs.set("sort", sort);
    api.get(`/products?${qs.toString()}`).then((r) => setProducts(r.data));
    // eslint-disable-next-line
  }, [category, search, sort, minPrice, maxPrice, params]);

  const applyPrice = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (minInput) next.set("min", minInput); else next.delete("min");
    if (maxInput) next.set("max", maxInput); else next.delete("max");
    setParams(next);
  };
  const clearPrice = () => {
    const next = new URLSearchParams(params);
    next.delete("min"); next.delete("max");
    setParams(next);
  };

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const setLoc = (next) => {
    const p = new URLSearchParams(params);
    locKeys.forEach((k) => { if (next[k]) p.set(k, next[k]); else p.delete(k); });
    setParams(p);
  };

  const submitSearch = (e) => { e.preventDefault(); setParam("search", searchInput); };
  const gridCls = `grid grid-cols-2 md:grid-cols-3 ${showFilters ? "xl:grid-cols-4" : "xl:grid-cols-6"} gap-4`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters"
        className="mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-800 text-white text-sm font-semibold hover:bg-emerald-900 transition-colors shadow-sm">
        <SlidersHorizontal className="w-4 h-4" /> {showFilters ? t("hide_filters") : t("show_filters")}
      </button>
      <div className={showFilters ? "grid lg:grid-cols-[260px_1fr] gap-8" : "grid grid-cols-1 gap-8"}>
        {showFilters && (
        <aside className="lg:sticky lg:top-24 h-fit bg-white border border-stone-200 rounded-2xl shadow-md overflow-hidden">
          <div className="bg-[#065F46] text-white px-5 py-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-mango-600 grid place-items-center"><SlidersHorizontal className="w-4 h-4" /></span>
            <span className="font-display font-extrabold text-lg tracking-tight">{t("filters")}</span>
          </div>
          <div className="p-5 space-y-6">
          <form onSubmit={submitSearch}>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("search_ph")}</label>
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={t("search_ph")} className="mt-2" data-testid="catalog-search" />
          </form>
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold block mb-2">{t("category")}</label>
            <div className="flex flex-col gap-1">
              <button onClick={() => setParam("category", "all")} data-testid="filter-cat-all"
                className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${category === "all" ? "bg-emerald-800 text-white font-semibold shadow-sm" : "text-stone-600 hover:bg-stone-100"}`}>{t("all")}</button>
              {cats.map((c) => (
                <button key={c.key} onClick={() => setParam("category", c.key)} data-testid={`filter-cat-${c.key}`}
                  className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${category === c.key ? "bg-emerald-800 text-white font-semibold shadow-sm" : "text-stone-600 hover:bg-stone-100"}`}>{c[lang]}</button>
              ))}
            </div>
          </div>
          <div className="bg-mango-600/5 border border-mango-600/20 rounded-xl p-3">
            <label className="text-xs uppercase tracking-wider text-mango-700 font-semibold block mb-2">{t("price_range")} (FCFA)</label>
            <form onSubmit={applyPrice} className="space-y-2">
              <div className="flex items-center gap-2">
                <Input type="number" min="0" value={minInput} onChange={(e) => setMinInput(e.target.value)} placeholder={t("min_price")} data-testid="filter-min-price" />
                <span className="text-stone-400">–</span>
                <Input type="number" min="0" value={maxInput} onChange={(e) => setMaxInput(e.target.value)} placeholder={t("max_price")} data-testid="filter-max-price" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" className="flex-1 bg-emerald-800 hover:bg-emerald-900 rounded-md" data-testid="apply-price">{t("apply")}</Button>
                {(minPrice || maxPrice) && <Button type="button" size="sm" variant="outline" onClick={clearPrice} data-testid="clear-price">×</Button>}
              </div>
            </form>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold block mb-2">{t("location")}</label>
            <LocationCascade value={loc} onChange={setLoc} />
          </div>
          </div>
        </aside>
        )}

        {/* Grid */}
        <div>
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">
              {category === "all" ? t("nav_catalog") : cats.find((c) => c.key === category)?.[lang] || t("nav_catalog")}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-500">{products?.length || 0} {t("results")}</span>
              <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
                <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("sort_new")}</SelectItem>
                  <SelectItem value="cheap">{t("sort_cheap")}</SelectItem>
                  <SelectItem value="expensive">{t("sort_exp")}</SelectItem>
                  <SelectItem value="popular">{t("sort_pop")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {products === null ? (
            <div className={gridCls}>{Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}</div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center text-stone-400" data-testid="no-results">{t("no_results")}</div>
          ) : (
            <div className={gridCls} data-testid="catalog-grid">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
